/**
 * Following the runs of one batch of uploads to their end. The server processes an audio file as
 * a run of its own, so a batch is as many runs as it has audio files, plus the one the client
 * starts for the rest. They are followed by one loop that asks once per turn for the whole batch
 * — the list of the case's runs answers it in one read — and that stops when it is aborted: the
 * page that started it was left. Pure, with the reads and the wait handed in.
 */
/** How often the runs are asked for. */
export const FOLLOW_INTERVAL_MS = 2000

/** How often they are asked before the loop gives up on runs that never end: about 5 minutes. */
export const FOLLOW_MAX_READS = 150

export interface FollowRunsOptions {
  /**
   * The status of each of these runs as the server says it now. A run it could not say this
   * turn is left out, and is asked for again.
   */
  read: (runIds: readonly string[]) => Promise<ReadonlyMap<string, string>>
  /** Ends the loop: it rejects with the signal's reason, and reads nothing more. */
  signal?: AbortSignal
  /** Settles after `ms`; rejects with the signal's reason once it is aborted. */
  wait?: (ms: number, signal?: AbortSignal) => Promise<void>
}

/**
 * Settles once every run has ended. Rejects when one did not complete — after all of them
 * ended, so the caller re-reads a settled case — when they do not end in time, and when it is
 * aborted.
 */
export async function followRuns(
  runIds: readonly string[],
  { read, signal, wait = abortableDelay }: FollowRunsOptions
): Promise<void> {
  const going = new Set(runIds)
  let failed: string | null = null
  for (let reads = 0; going.size > 0 && reads < FOLLOW_MAX_READS; reads++) {
    await wait(FOLLOW_INTERVAL_MS, signal)
    const statuses = await read([...going])
    signal?.throwIfAborted()
    for (const [runId, status] of statuses) {
      if (status === 'running' || !going.delete(runId)) continue
      if (status !== 'completed') failed ??= status
    }
  }
  if (going.size > 0) throw new Error('Extraction timed out')
  if (failed !== null) throw new Error(`Extraction ${failed}`)
}

/** Settles after `ms`; rejects with the signal's reason as soon as it is aborted, with no timer left. */
export function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason)
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      reject(signal?.reason)
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

interface RunStatus {
  id: string
  status: string
}

export interface RunReads {
  /** The newest runs of the case (`GET /extraction-runs`). */
  list: () => Promise<readonly RunStatus[]>
  /** One run by its id (`GET /extraction-runs/{id}`). */
  one: (runId: string) => Promise<RunStatus>
}

/**
 * The `read` of {@link followRuns} over the API: one list of the case's runs answers for the
 * whole batch. The list names the newest runs only, so a run it no longer names is asked for by
 * itself. A read that fails is reported and says nothing of its runs — a failed read is no
 * failed run — so they are asked for again on the next turn.
 */
export function runStatusReader(
  reads: RunReads,
  onError: (error: unknown) => void = () => undefined
): FollowRunsOptions['read'] {
  return async runIds => {
    const statuses = new Map<string, string>()
    try {
      const listed = new Map((await reads.list()).map(run => [run.id, run.status]))
      const unlisted: string[] = []
      for (const runId of runIds) {
        const status = listed.get(runId)
        if (status === undefined) unlisted.push(runId)
        else statuses.set(runId, status)
      }
      for (const runId of unlisted) statuses.set(runId, (await reads.one(runId)).status)
    } catch (error) {
      onError(error)
    }
    return statuses
  }
}
